import * as _ from "lodash";
import {
    AttributeFilterType,
    DataLocation,
    IdsAttribute,
    IdsFilterSpec,
    IdsMetric,
    IdsNode,
    IdsRow,
    IMetricDataSet,
    IMetricDataSetProvider
} from "./PA.MetricDataSet.IF";
import { IProgressProvider, MessageType } from "../PA.Core/PA.Core.IF.Basic";
import { BehaviorSubject } from "rxjs";
import { ProgressProvider } from "../PA.Core/Basic/Progress";
import { ExecutionSerializer } from "../PA.Core/Basic/ExecutionSerializer";
import { DateUtils } from "../PA.Core/Basic/Dates";
import { MessageProvider } from "../PA.Core/Basic/Messages";
import { ErrorMessage_restate, ExceptionMessage_restate } from "../PA.Core/Basic/MessageUtils";
import { ExpressionUtils } from "../PA.Core/Basic/ExpressionUtils"
import { sha1 } from "object-hash"
import { Index } from "../PA.Core/Basic/Index"
import { DataSetUtils } from "./DataSetUtils"

const bDebugSerializers = false;
const rowThresholdCountDefault = 5000;

export interface IMetricDataSetUnionOptions {
    name?: string;
    attributes?: string[];
    /** Defines the metrics that will be included in the union of the datasets */
    metrics?: string[];
    rowThresholdCountDefault?: number;
    dataLoadingPaused?: boolean;
}

interface ILoadScope {
    attributes: string[];
    metrics: string[];
    filters: IdsFilterSpec[];
    rowThresholdCount: number;

    attributeHash: string;
    metricHash: string;
    filterHash: string;
}

const emptyScope: ILoadScope = {
    attributes: [],
    metrics: [],
    filters: [],
    rowThresholdCount: rowThresholdCountDefault,

    attributeHash: "",
    metricHash: "",
    filterHash: ""
}

/** Defines an IMetricDataSet that will contribute to a MetricDataSetUnion */
export interface IdsUnionItem {
    ds: IMetricDataSet,
    /** This alias Id will be prefixed to all metrics coming from the ds property, creating new metric names */
    aliasId: string
}

/** Contains the unioned data for one row in the dataset
  It is a map of attribute Ids to either string or number or Date
  It contains a map of aliasId -> IdsRow objects (which contain metrics for a row)
  */
interface IdsUnionRow extends IdsRow {
    // Attributes
    [attributeId: string]: any
    /** Map of aliasIds to the underlying IdsRow that contains an IdsUnionItem's data */
    dctRow: {[aliasId: string]: IdsRow}
    keyStore: {[keyId: string]: any}
}

/** One of the datasets that will contribute to the final MetricDataSetUnion
 * Includes on its attributes */
interface IUnionDataSet {
    ds: IMetricDataSet,
    /** Prefix that is prepended to the underlying metric and is then used for metric names in this IUnionDataSet */
    aliasId: string
    dctAttributes: {[attributeId: string]: IdsAttribute}
    dctMetrics: {[srcMetricId: string]: IdsMetric}
    queueScope: ILoadScope,
    pushedScope: ILoadScope
}

/** Contains information on the dataset prefix from which the value is read
 * and the underlying metric on which it is based */
interface IUnionMetric {
    metricId: string, // Union Metric Id
    metric: IdsMetric,
    srcMetricId: string,
    /** Alias that comes from the IdsUnionItem from which this metric is created  */
    srcAliasId: string,
    /** The dataset from which this metric is taken */
    srcDataSet: IUnionDataSet,
}

/* Helper functions
* */
function isArrEqual(a: string[], b: string[]): boolean {
    if (_.difference(a, b).length || _.difference(b, a).length)
        return false
    return true
}

export class MetricDataSetUnion
    implements
        IMetricDataSet
{
    private dataSets: IUnionDataSet[];
    private createOptions: IMetricDataSetUnionOptions;
    private dataProvider: IMetricDataSetProvider;

    // Metadata from Data Provider
    private dctAttributes: { [attributeId: string]: IdsAttribute };
    private dctMetrics: { [metricId: string]: IUnionMetric };
    private dctComputedMetrics: { [metricId: string]: IdsMetric };

    // For Initialization
    private name: string;
    private loadComplete: boolean;
    private onLoadComplete: Promise<void>;
    private initMessageProvider: MessageProvider;

    // For Data Loading
    private loadProgressProvider: IProgressProvider;
    private queueScope: ILoadScope;
    private pushedScope: ILoadScope;
    private loadedScope: ILoadScope;

    private bDataLoadingPaused: boolean;

    /** Contains its own set of IdsUnionRows that represent the last-filtered state. References are not shared with allRows */
    private filteredRows: IdsUnionRow[];
    private onFilteredRowsChanged: BehaviorSubject<IMetricDataSet>;
    /** Contains its own set of every IdsUnionRow, excluding client-side filters. References are not shared with filteredRows */
    private allRows: IdsUnionRow[];
    private onAllRowsChanged: BehaviorSubject<IMetricDataSet>;
    private dctUniqueLocalValues: { [attributeId: string]: any[] };
    private dctUniqueLocalNodes: { [attributeId: string]: IdsNode[] };
    private dctUniqueRemoteValues: { [attributeId: string]: any[] };

    private onFilterChanged: BehaviorSubject<IMetricDataSet>;

    private esSyncChildStructureData: ExecutionSerializer;
    private esUpdateUnionDataSet: ExecutionSerializer;

    constructor(unionItems :IdsUnionItem[], options?: IMetricDataSetUnionOptions) {
        this.dataSets = []
        this.createOptions = options || {};
        this.name = options?.name || "Union Data Set";

        this.dataProvider = undefined;
        _.forEach(unionItems, (ui) => {
            this.dataSets.push({
                aliasId: ui.aliasId,
                ds: ui.ds,
                dctAttributes: {},
                dctMetrics: {},
                queueScope: _.cloneDeep(emptyScope),
                pushedScope: _.cloneDeep(emptyScope)
            })
        })

        // init
        this.loadComplete = false;
        this.onLoadComplete = undefined;
        this.initMessageProvider = new MessageProvider();

        // Data Loading
        this.loadProgressProvider = new ProgressProvider();
        this.filteredRows = [];
        this.onFilteredRowsChanged = new BehaviorSubject<IMetricDataSet>(undefined);
        this.allRows = [];
        this.onAllRowsChanged = new BehaviorSubject<IMetricDataSet>(undefined);
        this.onFilterChanged = new BehaviorSubject<IMetricDataSet>(undefined);
        this.dctUniqueLocalValues = {}
        this.dctUniqueLocalNodes = {}
        this.dctUniqueRemoteValues = {}

        this.esSyncChildStructureData = new ExecutionSerializer(this, this.syncChildStructureData.bind(this), [{}], 500, bDebugSerializers);
        this.esUpdateUnionDataSet = new ExecutionSerializer(this, this.updateUnionDataSet.bind(this), [{}], 500, bDebugSerializers);

        this.bDataLoadingPaused = false;
        if (_.isBoolean(this.createOptions?.dataLoadingPaused))
            this.bDataLoadingPaused = this.createOptions?.dataLoadingPaused;

        this.queueScope = _.cloneDeep(emptyScope);
        this.loadedScope = _.cloneDeep(emptyScope);
        this.pushedScope = _.cloneDeep(emptyScope);

        if (_.isArray(this.createOptions?.attributes) && !_.isEmpty(this.createOptions?.attributes))
            this.queueScope.attributes = _.clone(this.createOptions.attributes);

        if (_.isArray(this.createOptions?.metrics) && !_.isEmpty(this.createOptions?.metrics))
            this.queueScope.metrics = _.clone(this.createOptions.metrics);

        if (_.isNumber(this.createOptions.rowThresholdCountDefault))
            this.queueScope.rowThresholdCount = this.createOptions.rowThresholdCountDefault;

        this.dctAttributes = {}
        this.dctMetrics = {}
        this.dctComputedMetrics = {}
    }

    get Name(): string {
        return this.name;
    }

    get bLoaded(): boolean {
        return this.loadComplete;
    }

    OnLoadComplete(): Promise<void> {
        const self = this;

        if (this.onLoadComplete)
            return this.onLoadComplete;

        let mp = self.initMessageProvider

        const rv = (this.onLoadComplete = (async () => {
            try {
                //
                //
                self.dataProvider = self.dataSets[0]?.ds?.DataProvider

                for(let ds of self.dataSets){
                    if(_.isEmpty(ds.ds)){
                        ErrorMessage_restate(mp, true, "UndefinedDS", "Undefined DataSet")
                        continue
                    }

                    if(!ds.ds.bLoaded)
                        await ds.ds.OnLoadComplete()

                    if(!ds.ds.bLoaded){
                        ErrorMessage_restate(mp, true, "NotLoadedDS", "Unable to load DataSet")
                        continue
                    }



                }

                ErrorMessage_restate(mp, _.isEmpty(self.dataSets), "NoDataSets", "No Data Sets")

                if(mp.countMessages(MessageType.Error))
                    return


                for(let uds of self.dataSets){
                    uds.dctAttributes = _.keyBy(uds.ds.getAvailableAttributes(), (a) => a.Id)
                    uds.dctMetrics = _.keyBy(uds.ds.getAvailableMetrics(), (a) => a.Id)
                }

                // Create Union Attributes for all attributes of the same name and type
                let attribs = _.values(self.dataSets[0].dctAttributes);
                for(let atr of attribs){
                    let attribMatch = true
                    for(let idx = 1; idx < self.dataSets.length; idx++){
                        let dsAtr = self.dataSets[idx].dctAttributes[atr.Id]
                        if(_.isEmpty(dsAtr) || (atr.Type != atr.Type))
                            attribMatch = false
                    }

                    if(attribMatch)
                        self.dctAttributes[atr.Id] = atr
                }

                // Create Union Metrics for all metrics using the Prefix
                const cloneMetric = (src: IdsMetric, newId: string): IdsMetric => {
                    let rv: IdsMetric = {
                        Id: newId,
                        Name: src.Name,
                        ShortName: src.ShortName,
                        SortOrder: src.SortOrder,
                        format: src.format
                    }
                    _.defaults(rv, src)
                    return rv
                }
                for(let uds of self.dataSets){
                    for(let mtr of _.values(uds.dctMetrics)){
                        let mtrId = `${uds.aliasId}${mtr.Id}`;
                        if(mtrId in self.dctMetrics){
                            console.log(`Duplicate Metric Ids ${mtrId}`)
                        } else {
                            self.dctMetrics[mtrId] = {
                                metricId: mtrId,
                                srcAliasId: uds.aliasId,
                                srcMetricId: mtr.Id,
                                srcDataSet: uds,
                                metric: cloneMetric(mtr, mtrId)
                            }
                        }
                    }

                }

                // Create Event Handlers
                for(let uds of self.dataSets) {
                    uds.ds.OnFilteredRowsChanged.subscribe(self.evtChildDataSetOnFilteredRowsChanged.bind(self))
                    uds.ds.OnAllRowsChanged.subscribe(self.evtChildDataSetOnAllRowsChanged.bind(self))
                    uds.ds.OnFilterChanged.subscribe(self.evtChildDataSetOnFilterChanged.bind(self))
                }

                self.loadComplete = true;
            } catch (e) {
                ExceptionMessage_restate(self.initMessageProvider, e);
            }
        })());

        return rv;
    }

    private evtChildDataSetOnFilteredRowsChanged(mds: IMetricDataSet): void {
        this.UpdateUnionDataSet();
    }

    private evtChildDataSetOnAllRowsChanged(mds: IMetricDataSet): void {
        this.UpdateUnionDataSet();
    }

    private evtChildDataSetOnFilterChanged(mds: IMetricDataSet): void {
        // I don't think I need it do anything
    }


    // Parent Data Provider, use to access full set of available metadata.
    get DataProvider(): IMetricDataSetProvider {
        return this.dataProvider;
    }

    // Loading Status API & Events
    //-- changing server side filters, thresholds or structure (metrics, attributes) will trigger Loading
    get LoadProgressProvider(): IProgressProvider {
        return this.loadProgressProvider;
    }

    get RowCountThreshold(): number {
        return this.queueScope.rowThresholdCount;
    }

    setRowCountThreshold(threshold: number): void {
        this.queueScope.rowThresholdCount = threshold;
        this.SyncChildStructureData();
    }

    forceReload(): void {
        _.forEach(this.dataSets, (uds) => {
            uds.ds.forceReload();
        })
        this.SyncChildStructureData();
    }

    // Metadata API
    getAttribute(attributeId: string): IdsAttribute {
        if (attributeId in this.dctAttributes) return this.dctAttributes[attributeId];
    }

    getMetric(metricId: string): IdsMetric {
        if (metricId in this.dctMetrics) return this.dctMetrics[metricId].metric;
        if (metricId in this.dctComputedMetrics) return this.dctComputedMetrics[metricId]
    }

    getAvailableAttributes(): IdsAttribute[] {
        return _.filter(_.values(this.dctAttributes), (atr) => !_.isEmpty(atr));
    }

    getAvailableMetrics(): IdsMetric[] {
        return _.map(_.filter(_.values(this.dctMetrics), (mtr) => !_.isEmpty(mtr?.metric)), (f) => f.metric);
    }

    getSymbolType(symbolId: string): "Metric" | "Attribute" | undefined {
        switch (true) {
            case (symbolId in this.dctMetrics):
                return "Metric";
            case (symbolId in this.dctAttributes):
                return "Attribute";
        }
    }

    addComputedAttributes(attribute: IdsAttribute | IdsAttribute[]): void {
        // Could add computed attribute support later
    }

    removeComputedAttributes(attribute: IdsAttribute | IdsAttribute[]): void {
        // Could add computed attribute support later
    }

    // This can take theh ComputedMetrics class.
    // take the logic that's adding stuff to the dictionary in the constructor and instead add it
    // to this method and call that after instantiating the union dataset
    addComputedMetrics(metric: IdsMetric | IdsMetric[]): void {
        if (!Array.isArray(metric)) metric = [metric]
        metric.forEach(m => {
            const id = m.Id
            if (!id || id === "") {
                ErrorMessage_restate(this.initMessageProvider, true, "MetricDSUnionAddComputedMetric", "Empty Metric Id")
                return
            }
            this.dctComputedMetrics[id] = m
        })
    }

    removeComputedMetrics(metric: IdsMetric | IdsMetric[]): void {
        if (!Array.isArray(metric)) metric = [metric]
        metric.forEach(m => {
            const id = m.Id
            if (!id || id === "") {
                ErrorMessage_restate(this.initMessageProvider, true, "MetricDSUnionRemoveComputedMetric", "Empty Metric Id")
                return
            }
            delete this.dctComputedMetrics[id]
        })
    }

    // Structure API
    getAttributes(bLoaded?: boolean): IdsAttribute[] {
        const arrIds = bLoaded ? this.loadedScope.attributes : this.queueScope.attributes;
        const attrs = _.map(arrIds, (a) => this.getAttribute(a));
        return _.filter(attrs, (a) => !_.isEmpty(a));
    }

    getMetrics(bLoaded?: boolean): IdsMetric[] {
        const mtrIds = bLoaded ? this.loadedScope.metrics : this.queueScope.metrics;
        const mtrs = _.map(mtrIds, (a) => this.getMetric(a));
        return _.filter(mtrs, (a) => !_.isEmpty(a));
    }

    setRowStructure(arrAttributeIds: string[], arrMetricIds: string[]): void {
        this.queueScope.attributes = _.clone(arrAttributeIds);
        this.queueScope.metrics = _.clone(arrMetricIds);
        this.SyncChildStructureData();
    }

    // Query Attribute Domain Values
    async getAttributeDistinctValues(
        attributeId: string,
        location: DataLocation = "LocalLoaded",
        valueFilterIDs?: string[],
    ): Promise<number[] | string[] | Date[]> {
        const attr = this.getAttribute(attributeId)
        if (_.isEmpty(attr)) return []

        switch (location) {
            case "LocalLoaded":
                if (attributeId in this.dctUniqueLocalValues) return _.clone(this.dctUniqueLocalValues[attributeId])

                this.updateLocalDistinctValues(attributeId, this.dctUniqueLocalValues, this.allRows)
                return _.clone(this.dctUniqueLocalValues[attributeId] || [])

            case "RemoteAvailable": {
                if (_.isEmpty(valueFilterIDs)) {
                    if (attributeId in this.dctUniqueRemoteValues)
                        return _.clone(this.dctUniqueRemoteValues[attributeId])

                    // Note: May not be atomic
                    await this.updateRemoteDistinctValues(attributeId);
                    return _.clone(this.dctUniqueRemoteValues[attributeId] || []);
                }
                // if there are value filters to apply,
                // then we need to not cache them and retrieve them from the dataprovider each time
                // NOTE: This can be optimized with a separate cache in the future
                const attr = this.getAttribute(attributeId)
                if (_.isEmpty(attr)) return []
                let indexer = function (s: unknown): string {
                    return <string>s
                }
                if (attr.Type == "date")
                    indexer = function (s: any): string {
                        return <string>DateUtils.convertToSAPBW(s)
                    }

                const dct: { [id: string]: any } = {}
                const valueFilters = _.filter(
                    this.queueScope.filters,
                    (a) => a.type == "static" || valueFilterIDs.includes(a.id),
                )
                const uniqueValues = await this.dataProvider.getDistinctValues(attributeId, valueFilters)
                _.forEach(uniqueValues, (id) => {
                    const idx = indexer(id)
                    if (idx in dct) return
                    dct[idx] = id
                })

                return _.values(dct)
            }

            default:
        }

        return []
    }


    async getAttributeNodes(
        attributeId: string,
        location: DataLocation = "LocalLoaded",
        valueFilterIDs?: string[]
    ): Promise<IdsNode[]> {
        const attr = this.getAttribute(attributeId);
        if (_.isEmpty(attr)) return [];

        if (attr.Type != "node") return [];

        const uniqueIds = await this.getAttributeDistinctValues(attributeId, location, valueFilterIDs);

        // The dataProvider does any caching for the Nodes
        const nodes = await this.dataProvider.getAttributeNodes(attributeId, <number[]>uniqueIds);

        return nodes;
    }

    // Helper function for Visualization Controls
    getLocalAttributeNodes(attributeId: string): IdsNode[] {
        return _.clone(this.dctUniqueLocalNodes[attributeId] || []);
    }

    getAttributeDistinctRowValues(attributeId: string, rows: IdsRow[]): number[] | string[] | Date[] {
        const dctNew: { [attributeId: string]: any[] } = {};
        this.updateLocalDistinctValues(attributeId, dctNew, rows);
        return _.clone(dctNew[attributeId] || []);
    }

    private updateLocalDistinctValues(
        attributeId: string,
        tgt: { [attributeId: string]: any[] },
        rows: IdsRow[]
    ): void {
        const attr = this.getAttribute(attributeId);
        if (_.isEmpty(attr)) return;

        const dct: { [id: string]: any } = {};
        let indexer = function(s: any): string {
            return <string>s;
        };
        if (attr.Type == "date")
            indexer = function(s: any): string {
                return <string>DateUtils.convertToSAPBW(s);
            };

        _.forEach(rows, (row) => {
            const atrValue = this.getRowAttribute(row, attributeId);
            const idx = indexer(atrValue);
            if (idx in dct) return;
            dct[idx] = atrValue;
        });
        tgt[attributeId] = _.values(dct);
    }

    private async updateRemoteDistinctValues(attributeId: string): Promise<void> {
        const attr = this.getAttribute(attributeId);
        if (_.isEmpty(attr)) return;

        const dct: { [id: string]: any } = {};
        let indexer = function(s: any): string {
            return <string>s;
        };
        if (attr.Type == "date")
            indexer = function(s: any): string {
                return <string>DateUtils.convertToSAPBW(s);
            };

        const staticFilters = _.filter(this.queueScope.filters, (a) => a.type == 'static')
        const uniqueValues = await this.dataProvider.getDistinctValues(attributeId, staticFilters);

        _.forEach(uniqueValues, (id) => {
            const idx = indexer(id);
            if (idx in dct) return;
            dct[idx] = id;
        });
        this.dctUniqueRemoteValues[attributeId] = _.values(dct);
    }


    // Data Row Filtering API & Events
    setFilter(filterSpec: IdsFilterSpec): void {
        this.setFilters([filterSpec]);
    }

    setFilters(filters: IdsFilterSpec[]): void {
        const flts = _.filter(filters, (a) => !_.isEmpty(a));
        const arrFilterIds = _.map(flts, (a) => a.id);
        const dctIds = _.keyBy(arrFilterIds, (a) => a);
        const removed = _.remove(this.queueScope.filters, (a) => a.id in dctIds);
        _.forEach(flts, (a) => {
            this.queueScope.filters.push(_.cloneDeep(a));
        });
        this.SyncChildStructureData();
    }

    clearFilters(filterId: string | string[]): void {
        let flts: string[] = [];
        if (_.isArray(filterId)) flts = filterId;
        else flts = [filterId];
        _.remove(flts, (a) => _.isEmpty(a));
        const dctIds = _.keyBy(flts, (a) => a);
        _.remove(this.queueScope.filters, (a) => a.id in dctIds);
        this.SyncChildStructureData();
    }

    clearFiltersOfTypes(filterTypes: AttributeFilterType[]): void {
        const dctIds = _.keyBy(filterTypes, (a) => a);
        _.remove(this.queueScope.filters, (a) => a.type in dctIds);
        this.SyncChildStructureData();
    }

    getFilter(filterId: string): IdsFilterSpec[] {
        return _.cloneDeep(_.filter(this.queueScope.filters, (a) => a.id == filterId));
    }

    getAllFilters(bLoaded?: boolean): IdsFilterSpec[] {
        return _.cloneDeep(bLoaded ? this.loadedScope.filters : this.queueScope.filters);
    }

    get OnFilterChanged(): BehaviorSubject<IMetricDataSet> {
        return this.onFilterChanged;
    }

    get FilteredRows(): IdsRow[] {
        return this.filteredRows;
    }

    get OnFilteredRowsChanged(): BehaviorSubject<IMetricDataSet> {
        return this.onFilteredRowsChanged;
    }

    get AllRows(): IdsRow[] {
        return this.allRows;
    }

    get OnAllRowsChanged(): BehaviorSubject<IMetricDataSet> {
        return this.onAllRowsChanged;
    }

    // Pause, Resume Data Loading
    pauseDataLoading(): void {
        this.bDataLoadingPaused = true;
    }

    resumeDataLoading(): void {
        this.bDataLoadingPaused = false;
        this.SyncChildStructureData();
    }

    get DataLoadingPaused(): boolean {
        return this.bDataLoadingPaused;
    }

    // Access to Row Attribute Values,  Metric Values and Custom Values
    getRowAttribute(row: IdsRow, attributeId: string): any {
        return (<any>row || {})[attributeId];
    }

    getRowMetric(row: IdsRow, metricId: string): number {
        let r = <IdsUnionRow>row

        if (metricId in this.dctComputedMetrics) {
            // if it's in this dictionary, then use the IdsMetric object to access the computed value
            const mtr = this.dctComputedMetrics[metricId]
            return mtr.getComputedValue(this, row)
        }

        if (metricId in this.dctMetrics) {
            const mtr = this.dctMetrics[metricId];

            let srcRow =  r?.dctRow?.[mtr.srcAliasId]
            return mtr.srcDataSet.ds.getRowMetric(srcRow, mtr.srcMetricId)
        }
        return 0
    }

    //-- Custom Data
    getRowData(row: IdsRow, keyId: string): any {
        return (<any>(row || {}))?.keyStore?.[keyId];
    }

    setRowData(row: IdsRow, keyId: string, value: any): void {
        if (_.isEmpty(row)) return undefined;
        let anyRow = <any>row;
        anyRow.keyStore = anyRow.keyStore || {};
        anyRow.keyStore[keyId] = value;
    }

    getRowAggregateMetric(rows: IdsRow[], metricId: string): number {
        const self = this

        const mtr = self.dctMetrics[metricId]?.metric
        if(_.isEmpty(mtr?.aggregateExpression))
            return undefined;

        const aggExpr = mtr.aggregateExpression;

        function getSymbol(row: IdsRow, symbolId: string): number {
            return self.getRowMetric(row, symbolId)
        }

        const aggMtrValue = ExpressionUtils.getAggregate(aggExpr, rows, getSymbol)
        return aggMtrValue

    }



    //-- Private Functions

    //--
    private SyncChildStructureData(): void {
        this.esSyncChildStructureData.Run();
    }

    // Important: This should only be called from the execution serializer
    // if there's a computed metric, then it needs to be ignored
    private async syncChildStructureData(): Promise<void> {
        const self = this

        if(self.bDataLoadingPaused)
            return

        const pp = this.loadProgressProvider;
        try {
            pp.start();

            let queueScope = _.cloneDeep(self.queueScope)
            let dsMetrics: {[aliasId: string]: string[]} = self.getUnionChildMetrics(queueScope.metrics)

            // Update The Queue Metrics
            let dsAttributes: {[attributeId: string]: IdsAttribute} = {}

            for(let atrId of queueScope.attributes) {
                if(atrId in self.dctAttributes)
                    dsAttributes[atrId] = self.dctAttributes[atrId]
            }

            // Update QueueScope
            for(let dsU of self.dataSets){
                dsU.queueScope.attributes = _.keys(dsAttributes)
                dsU.queueScope.metrics = dsMetrics[dsU.aliasId] || []
                let allFilters = _.clone(queueScope.filters)
                allFilters = _.sortBy(allFilters, [(a) => a.type, (a) => a.id, (a) => sha1(a.condition)]);
                dsU.queueScope.filters = allFilters;
                dsU.queueScope.rowThresholdCount = self.RowCountThreshold
                // TODO: Convert Metric Names to local metric

                // Attribute names are the same
            }
            // Push any changes that are needed
            for(let dsU of self.dataSets){
                // Check if queued Structure different

                // Check if Load Scope Changes
                let dsQueuedMetrics = _.map(dsU.ds.getMetrics(false), (a) => a.Id)
                let dsQueuedAttributes = _.map(dsU.ds.getAttributes(false), (a) => a.Id)
                let missingMetrics = _.difference(dsU.queueScope.metrics, dsQueuedMetrics)

                let bStructureMismatch = (!isArrEqual(dsQueuedAttributes, dsU.queueScope.attributes)) || _.isEmpty(missingMetrics)

                if(bStructureMismatch || !this.LoadScope_equals(dsU.pushedScope, dsU.queueScope)){
                    let bPaused = dsU.ds.DataLoadingPaused
                    dsU.ds.pauseDataLoading()

                    self.queueScope.metrics = _.union(self.queueScope.metrics, missingMetrics.map((a) => `${dsU.aliasId}${a}`))
                    dsU.ds.setRowStructure(dsU.queueScope.attributes, _.union(dsU.queueScope.metrics, missingMetrics))

                    // Remove Filters
                    let prevFilterIds = _.map(dsU.pushedScope.filters, (a) => a.id)
                    let curFilterIds = _.map(dsU.queueScope.filters, (a) => a.id)
                    let removedFilters = _.difference(prevFilterIds, curFilterIds)
                    dsU.ds.clearFilters(removedFilters)
                    dsU.ds.setFilters(dsU.queueScope.filters)

                    if(dsU.ds.RowCountThreshold != dsU.queueScope.rowThresholdCount)
                        dsU.ds.setRowCountThreshold(dsU.queueScope.rowThresholdCount)

                    if(!bPaused)
                        dsU.ds.resumeDataLoading()

                    dsU.pushedScope = _.cloneDeep(dsU.queueScope)
                }

            } // Next Dataset

            self.pushedScope = _.cloneDeep(self.queueScope)

            pp.finish();
        } catch (err) {
            pp.exception(err);
        }
    }

    private UpdateUnionDataSet(): void {
        this.esUpdateUnionDataSet.Run();
    }

    // Important: This should only be called from the execution serializer
    private async updateUnionDataSet(): Promise<void> {
        const self = this

        if(_.isEmpty(self.pushedScope.metrics))
            return

        //
        // Using last Pushed Metrics
        // 1. Create Index
        // 2. Get dsMetrics using last pushed Scope
        // 3. Check if DS Structure Match
        // 4. If matching record, merge into final results
        // If at least 1 match,
        // 5. Update Loaded Scope
        // 6. Raise Events

        try {

            // queued scope is what the enduser is calling set structure with. it's what the user wants
            // pushed scope is what was last pushed to the child datasets. the structure is cascaded to all the children to tell them the metrics that this union dataset will need
            // loaded scope is the metric data that has been unioned together and is ready for use
            let pushedScope = _.cloneDeep(self.pushedScope)

            let arrAttribs = _.map(pushedScope.attributes, (atrId) => self.dctAttributes[atrId])
            // Create the index,
            let indexers: ((r: IdsUnionRow) => string)[] = []
            _.forEach(arrAttribs, (atr) => {
                let atrId = atr.Id
                indexers.push((r: IdsUnionRow) => DataSetUtils.Attribute_getIndexingValue(atr, self.getRowAttribute(r, atrId)))
            })
            let indexAllRows = new Index<IdsUnionRow>(indexers, false)
            let indexFilterRows = new Index<IdsUnionRow>(indexers, false)

            // Get Data Set Metrics
            let dsMetrics: {[aliasId: string]: string[]} = self.getUnionChildMetrics(pushedScope.metrics)

            let matchCount = 0
            let mismatchCount = 0

            for(let dsU of self.dataSets){
                let loadedAttributes = dsU.ds.getAttributes(true)
                let loadedAttributeIds = _.map(loadedAttributes, (a) => a.Id)
                let attributesMatch = isArrEqual(loadedAttributeIds, pushedScope.attributes)

                let loadedMetrics = dsU.ds.getMetrics(true)
                let loadedMetricIds = _.map(loadedMetrics, (a) => a.Id)
                let requiredMetricIds = dsMetrics[dsU.aliasId] || []
                let missingMetricIds = _.difference(requiredMetricIds, loadedMetricIds)
                let metricMatch = _.isEmpty(missingMetricIds)

                if(!(metricMatch && attributesMatch)){
                    mismatchCount++
                    return
                }
                matchCount++

                let dsAllRows = dsU.ds.AllRows
                let dsFilterRows = dsU.ds.FilteredRows

                const addRowToIndex = (srcRow: IdsRow, idx: Index<IdsUnionRow>) => {
                    let idxKeys: string[] = []
                    // Get Keys from Source Row; each attribute is included as a key to create a distinct row
                    _.forEach(arrAttribs, (atr) => {
                        idxKeys.push(DataSetUtils.Attribute_getIndexingValue(atr, dsU.ds.getRowAttribute(srcRow, atr.Id)))
                    })

                    let tgtRow = <IdsUnionRow> idx.get(idxKeys)
                    if(!_.isEmpty(tgtRow)){
                        tgtRow.dctRow[dsU.aliasId] = srcRow
                        return
                    } else {
                        // Create
                        let newRow: IdsUnionRow = {dctRow: {}, keyStore: {}}
                        newRow.dctRow[dsU.aliasId] = srcRow
                        for(let atr of arrAttribs){
                            newRow[atr.Id] = dsU.ds.getRowAttribute(srcRow, atr.Id)
                        }

                        idx.add(newRow)
                    }
                }

                // Add Rows to Indexes
                _.forEach(dsAllRows, (r) => addRowToIndex(r, indexAllRows))
                _.forEach(dsFilterRows, (r) => addRowToIndex(r, indexFilterRows))

            } // Next Union Data Set

            // Restate Loaded DataSet if at Least 1 dataset matches
            if(matchCount == 0)
                return

            let allRows = <IdsUnionRow[]> indexAllRows.values()
            let filteredRows = <IdsUnionRow[]> indexFilterRows.values()

            // Update Local Distinct Values and Nodes
            let dctUniqueLocalValues: { [attributeId: string]: any[] } = {}
            let dctUniqueLocalNodes: { [attributeId: string]: IdsNode[] } = {}
            for(let atr of arrAttribs) {
                self.updateLocalDistinctValues(atr.Id, dctUniqueLocalValues, allRows)
                dctUniqueLocalNodes[atr.Id] = await this.dataProvider.getAttributeNodes(
                    atr.Id,
                    dctUniqueLocalValues[atr.Id]
                );
            }

            // the computed metrics need to be evaluated
            // get all the computed metrics from pushedScope.metrics
            // for each of those, call the function that sets the metric value (either setComputedValuesAllRows or the other one)
            // if the setComputedFilteredRows function is set, then apply only to the filtered rows. remember, it's for creating aggregate metrics using filtered data, so it would be inaccurate to apply the function to the all dataset and would overwrite the metric key
            // if the setComputedValuesAllRows is set, then apply to both all and filtered rows.
            const computedMtrs = pushedScope.metrics.filter(id => id in this.dctComputedMetrics)
            for (const mtrId of computedMtrs) {
                const mtr = this.getMetric(mtrId)
                if (_.isFunction(mtr.setComputedValuesAllRows)) {
                    await mtr.setComputedValuesAllRows(
                        this,
                        this.pushedScope.attributes,
                        this.pushedScope.metrics,
                        allRows
                    )
                    await mtr.setComputedValuesAllRows(
                        this,
                        this.pushedScope.attributes,
                        this.pushedScope.metrics,
                        filteredRows
                    )
                } else {
                    if (!_.isFunction(mtr.setComputedValuesFilteredRows)) {
                        ErrorMessage_restate(this.initMessageProvider, true, "UpdateUnionDataSet", `Computed Metric ${mtr.Id} does not have a setComputedValuesFilteredRows function`)
                        continue
                    }
                    await mtr.setComputedValuesFilteredRows(
                            this,
                            this.pushedScope.attributes,
                            this.pushedScope.metrics,
                            filteredRows,
                            allRows
                        )
                }
            }

            // This needs to be atomic, nothing async after this point
            let pushedStaticFilters = _.filter(pushedScope.filters, (flt: IdsFilterSpec) => flt.type == "static")
            let loadedStaticFilters = _.filter(self.loadedScope.filters, (flt: IdsFilterSpec) => flt.type == "static")
            pushedStaticFilters = _.sortBy(pushedStaticFilters, (flt: IdsFilterSpec) => flt.id)
            loadedStaticFilters = _.sortBy(loadedStaticFilters, (flt: IdsFilterSpec) => flt.id)
            let bStaticScopeChanged = sha1(pushedStaticFilters) != sha1(loadedStaticFilters)

            // now that things are loaded, make that information known
            self.loadedScope = _.cloneDeep(pushedScope)
            self.allRows = allRows
            self.filteredRows = filteredRows
            self.dctUniqueLocalValues = dctUniqueLocalValues
            self.dctUniqueLocalNodes = dctUniqueLocalNodes
            if (bStaticScopeChanged)
                self.dctUniqueRemoteValues = {};

            // Raise Events
            self.OnAllRowsChanged.next(self)
            self.OnFilteredRowsChanged.next(self)
            self.OnFilteredRowsChanged.next(self)

        } catch (err) {
            ErrorMessage_restate(self.initMessageProvider, true, "UpdateUnionDataSet", err)
        }
    }

    private getUnionChildMetrics(arrMetricIds: string[]): { [aliasId: string]: string[] } {
        const self = this
        let dataSetMetrics: { [aliasId: string]: string[] } = {}

        /** Adds a computed metric's required metrics to the dataSetMetrics dictionary.
         * This method makes a recursive call to allow for computed metrics to be made of computed metrics */
        const processRequiredMetrics = (mtr: IdsMetric) => {
            const unionedMetrics = this.getUnionChildMetrics(mtr.requiredMetricsIds)
            for (const key in unionedMetrics) {
                if (key in dataSetMetrics) {
                    dataSetMetrics[key] = _.union(dataSetMetrics[key], unionedMetrics[key])
                } else {
                    dataSetMetrics[key] = unionedMetrics[key]
                }
            }
        }

        for (let mtrId of arrMetricIds) {
            // handle the case where the metric is computed
            if (mtrId in self.dctComputedMetrics) {
                processRequiredMetrics(self.dctComputedMetrics[mtrId])
                continue
            }

            if (!(mtrId in self.dctMetrics))
                continue
            let mtrU = self.dctMetrics[mtrId]

            if (mtrU.srcAliasId in dataSetMetrics) {
                if (!_.includes(dataSetMetrics[mtrU.srcAliasId], mtrU.srcMetricId))
                    dataSetMetrics[mtrU.srcAliasId].push(mtrU.srcMetricId)
            } else {
                dataSetMetrics[mtrU.srcAliasId] = [mtrU.srcMetricId]
            }
        }

        return dataSetMetrics
    }



    private LoadScope_equals(cur: ILoadScope, next: ILoadScope): boolean {
        this.LoadScope_updateHash(next);

        if (_.isEmpty(cur) || _.isEmpty(next)) return false;


        if (cur.attributeHash != next.attributeHash) return false;


        if (cur.metricHash != next.metricHash) return false;



        if (cur.filterHash != next.filterHash) return false;

        if (cur.rowThresholdCount != next.rowThresholdCount) return false;

        return true;
    }

    private LoadScope_updateHash(ls: ILoadScope) {
        if (_.isEmpty(ls)) return;
        ls.attributeHash = sha1(ls.attributes);
        ls.metricHash = sha1(ls.metrics);
        ls.filterHash = sha1(ls.filters);
    }


}
